import type { OperatingSystem } from '../types';

/**
 * Screenshot predecessor key combinations by operating system.
 * These are the modifier key combinations that precede screenshot keys.
 * We detect these BEFORE the final key is pressed since the OS intercepts the full combo.
 */
export interface PredecessorCombo {
    /** Whether Meta key must be pressed (Cmd on Mac, Win key on Windows) */
    meta?: boolean;
    /** Whether Ctrl key must be pressed */
    ctrl?: boolean;
    /** Whether Shift key must be pressed */
    shift?: boolean;
    /** Whether Alt key must be pressed */
    alt?: boolean;
}

/**
 * Screenshot predecessor combinations by operating system.
 * When these modifier combinations are detected, we show the overlay
 * preemptively before the user can complete the screenshot shortcut.
 */
export const SCREENSHOT_PREDECESSORS: Record<OperatingSystem, PredecessorCombo[]> = {
    macos: [
        // Cmd+Shift (precedes Cmd+Shift+3/4/5)
        { meta: true, shift: true },
        // Cmd+Ctrl+Shift (precedes Cmd+Ctrl+Shift+3/4 for clipboard)
        { meta: true, ctrl: true, shift: true }
    ],
    windows: [
        // Win+Shift (precedes Win+Shift+S for Snipping Tool)
        { meta: true, shift: true },
        // Ctrl+Shift (precedes Ctrl+Shift+S and other screenshot tool shortcuts)
        { ctrl: true, shift: true }
        // Win key alone (precedes Win+PrintScreen) - but this is too aggressive
        // Alt alone (precedes Alt+PrintScreen) - also too aggressive
    ],
    linux: [
        // Shift alone precedes Shift+PrintScreen, but too aggressive
        // Most Linux screenshot tools use PrintScreen directly which we can't catch
        // We'll still try to catch Shift being held as a weaker signal
        { shift: true }
    ],
    // iOS and some mobile WebViews report as "unknown" (no mac/win/linux in UA).
    // A Shift-only predecessor is wrong there (Shift is for capitals) and is not
    // a reliable screenshot signal.
    unknown: []
};

/**
 * Checks if the current keyboard state matches any screenshot predecessor combination.
 * This is called on keydown to detect when screenshot modifier keys are being held.
 */
export function isScreenshotPredecessor(event: KeyboardEvent, os: OperatingSystem): boolean {
    const combos = SCREENSHOT_PREDECESSORS[os];

    for (const combo of combos) {
        if (matchesPredecessor(event, combo)) {
            return true;
        }
    }

    return false;
}

/**
 * Checks if the current keyboard event state matches a predecessor combo
 */
function matchesPredecessor(event: KeyboardEvent, combo: PredecessorCombo): boolean {
    // Check Meta (Cmd/Win) - must match if specified
    if (combo.meta && !event.metaKey) {
        return false;
    }

    // Check Ctrl - must match if specified
    if (combo.ctrl && !event.ctrlKey) {
        return false;
    }

    // Check Shift - must match if specified
    if (combo.shift && !event.shiftKey) {
        return false;
    }

    // Check Alt - must match if specified
    if (combo.alt && !event.altKey) {
        return false;
    }

    // At least one modifier must be pressed for this to be a valid predecessor
    const hasRequiredModifiers =
        (combo.meta ? event.metaKey : true) &&
        (combo.ctrl ? event.ctrlKey : true) &&
        (combo.shift ? event.shiftKey : true) &&
        (combo.alt ? event.altKey : true);

    // Make sure we're not triggering on just any key -
    // the pressed key should be one of the modifier keys themselves
    const isModifierKey = ['Meta', 'Shift', 'Control', 'Alt'].includes(event.key);

    return hasRequiredModifiers && isModifierKey;
}
